import {customElement, property} from "lit/decorators.js";
import {FORM_ELEMENT_INIT_EVENT, type FormInitEvent, initStepEvent} from "./formEvents";
import type {FormInputElement} from "./components/formInputElement";
import {ValidationError} from "./validations/validations";
import {ScrollService} from "../../common/scroll";
import {resolve} from "../../container";
import {UnLitElement} from "../../common/elements";

export type StepData = {
    id: string;
    label: string;
    nextButtonLabel: string;
    prevButtonLabel: string;
    validate: ((silent?: boolean) => boolean);
};

@customElement("eop-form-step")
export class EopFormStep extends UnLitElement {

    @property({attribute: "next-label"})
    private nextLabel: string;
    @property({attribute: "prev-label"})
    private prevLabel: string;
    @property({attribute: "step-id"})
    private stepId: string;
    @property({attribute: "label"})
    private label: string;

    private validations: ((silent: boolean) => void)[];

    public constructor(
        private scrollService: ScrollService = resolve(ScrollService)
    ) {
        super();
        this.validations = [];
        this.addEventListener(FORM_ELEMENT_INIT_EVENT, event => {
            const eventData = (event as FormInitEvent).detail;
            this.validations.push(eventData.validate);
        });
    }

    public connectedCallback(): void {
        super.connectedCallback();

        this.dispatchEvent(initStepEvent({
            id: this.stepId,
            label: this.label,
            nextButtonLabel: this.nextLabel,
            prevButtonLabel: this.prevLabel,
            validate: silent => this.validate(silent)
        }));
    }

    public disconnectedCallback(): void {
        this.validations = [];
        super.disconnectedCallback();
    }

    private validate(silent: boolean = false): boolean {
        const invalidElements: FormInputElement<any>[] = [];

        for (const validation of this.validations) {
            try {
                validation(silent);
            } catch (e) {
                if (!(e instanceof ValidationError)) {
                    throw e;
                }
                invalidElements.push(e.invalidElement);
            }
        }

        if (invalidElements.isEmpty()) {
            return true;
        }

        if (!silent) {
            this.scrollToHighestElement(invalidElements);
        }
        return false;
    }

    private scrollToHighestElement(elements: FormInputElement<any>[]): void {
        const element: FormInputElement<any> = elements.clone().sort((prev, curr) => prev.offsetTop - curr.offsetTop).first()!;
        void this.scrollService.scrollToElement(element);
        if (element.focusInput !== undefined) {
            element.focusInput();
        }
    }
}